import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, Typography } from '@mui/material';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { MouseEvent, useState } from 'react';
import { TaskService } from 'service/task.service';
import zeplinColor from 'theme/Color';

type TasksSettingsProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  task: {
    id?: string;
    subject?: string;
    status?: string;
    link?: string;
    source?: string;
    [key: string]: any;
  };
  onViewDetails: () => void;
  onEdit: () => void;
  onStatusChange: () => void;
  onDelete: () => void;
};

const STATUS_OPTIONS = [
  { label: 'ToDo', value: 'To Do' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Done', value: 'Done' },
];

export const TasksSettings = ({
  anchorEl,
  onClose,
  task,
  onViewDetails,
  onEdit,
  onStatusChange,
  onDelete,
}: TasksSettingsProps) => {
  const [statusSubmenuAnchor, setStatusSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const open = Boolean(anchorEl);
  const statusSubmenuOpen = Boolean(statusSubmenuAnchor);

  const handleStatusSubmenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setStatusSubmenuAnchor(event.currentTarget);
  };

  const handleStatusSubmenuClose = () => {
    setStatusSubmenuAnchor(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (isUpdatingStatus || !task?.id) return;

    if (newStatus.toLowerCase() === task?.status?.toLowerCase()) {
      handleStatusSubmenuClose();
      return;
    }

    setIsUpdatingStatus(true);
    try {
      let assignedTo: string | string[] | undefined;

      if (Array.isArray(task?.assignedTo) && task.assignedTo.length > 0) {
        assignedTo = task.assignedTo.map((assignee: any) => assignee?.Id).filter(Boolean);
      } else if (
        typeof task?.assignedTo === 'object' &&
        task?.assignedTo !== null &&
        'Id' in task.assignedTo
      ) {
        assignedTo = task.assignedTo.Id;
      } else if (typeof task?.assignedTo === 'string') {
        assignedTo = task.assignedTo;
      } else if (task?.assignTo?.Id) {
        assignedTo = task.assignTo.Id;
      }

      const updateData: any = {
        subject: task?.subject,
        status: newStatus,
        priority: Array.isArray(task?.priority) ? task?.priority[0] : task?.priority || 'Normal',
      };

      if (task?.dueDate || task?.date) {
        updateData.dueDate = task?.dueDate || task?.date;
      }

      if (assignedTo) {
        updateData.assignedTo = assignedTo;
      }

      if (task?.description) {
        updateData.description = task?.description;
      }

      if (task?.recordTypeName) {
        updateData.recordTypeName = task.recordTypeName;
      }

      await TaskService.updateTask(task.id, updateData);

      handleStatusSubmenuClose();
      onClose();
      onStatusChange();
    } catch (err: any) {
      console.error('Failed to update task status:', err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleViewDetails = () => {
    onViewDetails();
    onClose();
  };

  const handleEdit = () => {
    onEdit();
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  return (
    <>
      <CustomPopOver
        setAnchorEl={onClose}
        anchorEl={anchorEl}
        id={open ? 'simple-popover' : undefined}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
        onClick={(e: any) => {
          e.stopPropagation();
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '16px',
            minWidth: '200px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '9px',
              cursor: 'pointer',
              position: 'relative',
            }}
            onClick={handleStatusSubmenuOpen}
          >
            <Box sx={{ display: 'flex', gap: '9px', alignItems: 'center', flex: 1 }}>
              <SwapHorizIcon sx={{ width: 18, height: 18, color: zeplinColor.BasicBlack }} />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Change status
              </Typography>
            </Box>
            <KeyboardArrowRightIcon sx={{ width: 16, height: 16, color: zeplinColor.BasicBlack }} />
          </Box>

          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleViewDetails}>
            <img src={'/assets/view.svg'} width={18} height={18} alt='View details' />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              View details
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleEdit}>
            <img src={'/assets/edit.svg'} width={18} height={18} alt='Edit task' />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Edit Task
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleDelete}>
            <img src={'/assets/delete-red.png'} width={18} height={18} alt='Delete' />
            <Typography variant='responsiveBody3' color={zeplinColor.Error}>
              Delete
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>

      <CustomPopOver
        setAnchorEl={handleStatusSubmenuClose}
        anchorEl={statusSubmenuAnchor}
        id={statusSubmenuOpen ? 'simple-popover' : undefined}
        sx={{
          borderRadius: '14px',
          boxShadow: 'rgba(0, 0, 0, 0.15)',
          marginLeft: '8px',
        }}
        vertical='top'
        horizontal='right'
        onClick={(e: any) => {
          e.stopPropagation();
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '12px 0',
            minWidth: '150px',
          }}
        >
          {STATUS_OPTIONS.map((option) => {
            const isSelected = option.value.toLowerCase() === task?.status?.toLowerCase();
            return (
              <Box
                key={option.value}
                sx={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? zeplinColor.Neutral4 : 'transparent',
                  '&:hover': {
                    backgroundColor: zeplinColor.Neutral4,
                  },
                }}
                onClick={() => handleStatusChange(option.value)}
              >
                <Typography
                  variant='responsiveBody3'
                  color={zeplinColor.BasicBlack}
                  sx={{
                    fontWeight: isSelected ? 500 : 400,
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CustomPopOver>
    </>
  );
};
